import { useIntl } from 'react-intl';

import { Icon } from '@/components/Icon';
import { KeyFeatures } from '@/components/KeyFeatures';
import { Visibility } from '@/components/Visibility';
import type { WatchAttributesFragment } from '@/graphql/saleor/api';
import type { PlaceholderType } from '@/integrations/contentful';
import { getDropdownValue } from '@/lib/attributes';
import type { Maybe } from '@/lib/types';
import { pdpMessages } from '@/messages/product';

import { useKeyFeatures } from './utils';

import { ReactComponent as YoutubeSvg } from '@/assets/youtube.svg';

import * as S from './KeyFeaturesSection.styles';

export interface KeyFeaturesOpts {
  heading?: string | null;
  product: Maybe<WatchAttributesFragment>;
  type: PlaceholderType;
  videoUrl?: string | null;
  withSectionMargin?: boolean;
}

export type KeyFeaturesSectionProps = KeyFeaturesOpts;

export const KeyFeaturesSection = ({
  heading,
  product,
  type,
  videoUrl,
  withSectionMargin,
}: KeyFeaturesSectionProps) => {
  const { formatMessage } = useIntl();
  const features = useKeyFeatures(product || {});
  const keyFeatures = product && features;
  const loading = product === undefined;
  const manualUrl = product?.manualUrl?.values[0]?.plainText ?? '';
  const waterResistanceTextComputed = getDropdownValue(
    product?.waterResistanceTextComputed,
  );

  return (
    <KeyFeatures type={type} withSectionMargin={withSectionMargin}>
      <KeyFeatures.Header heading={heading}>
        <KeyFeatures.RightContent>
          {manualUrl && (
            <S.SwissMade data-test-id='KeyFeaturesSectionSwissMade'>
              {formatMessage(pdpMessages.swissMade)}
            </S.SwissMade>
          )}
          {manualUrl && (
            <S.LinkWrapper
              data-testid='KeyFeaturesSectionUserManualDownloadLink'
              href={manualUrl}
              target='_blank'
            />
          )}
          {videoUrl && (
            <S.LinkWrapper
              data-testid='KeyFeaturesSectionVideoLink'
              href={videoUrl}
              label={formatMessage(pdpMessages.watchHowToVideos)}
              leftContent={<YoutubeSvg />}
              openUrlInNewTab
              size='l'
              variant='link'
              width='auto'
            >
              <Icon name='chevronRight' size='s' />
            </S.LinkWrapper>
          )}
        </KeyFeatures.RightContent>
      </KeyFeatures.Header>
      <KeyFeatures.Features
        keyFeatures={keyFeatures}
        loading={loading}
        waterResistanceTextComputed={waterResistanceTextComputed}
      />
      {manualUrl && (
        <S.LinkWrapperMobile
          data-testid='KeyFeaturesSectionUserManualDownloadLinkMobile'
          href={manualUrl}
          target='_blank'
        />
      )}
      {videoUrl && (
        <S.LinkWrapperMobile
          data-testid='KeyFeaturesSectionVideoLinkMobile'
          href={videoUrl}
          label={formatMessage(pdpMessages.watchHowToVideos)}
          leftContent={<YoutubeSvg />}
          size='l'
          target='_blank'
          variant='link'
          width='auto'
        >
          <Icon name='chevronRight' size='s' />
        </S.LinkWrapperMobile>
      )}
      <Visibility mdVisible>
        <S.Line />
      </Visibility>
    </KeyFeatures>
  );
};

KeyFeaturesSection.displayName = 'KeyFeaturesSection';
